import { type FC, type ReactNode } from 'react';
import classnames from 'classnames';
import { graphql, useFragment } from 'react-relay';

import { FormattedMessage, FormattedNumber } from 'dibs-react-intl';
import { Link } from 'dibs-elements/exports/Link.tsx';
import HeadingLevel from 'dibs-controlled-heading/exports/HeadingLevel.tsx';
import { TradeBenefitsAccordionItem } from '../../TradeBenefitsAccordionItem.tsx';
import TradeBenefitsNextStatus from './TradeBenefitsNextStatus.tsx';

import getBuyerSupportLink from 'dibs-support-link/exports/getBuyerSupportLink.ts';
import {
    CURRENT_TIER_SECTION_ID,
    PURCHASE_PROTECTION_SECTION_ID,
    NET_PRICE_PERCENTAGE,
    SILVER_PLUS_CODES,
    TRADE_STATUS,
    CUSTOMER_SUPPORT_URL,
    TRADE_SALES_EMAIL,
    type TradeStatusCode,
} from '../../tradeBenefitsConstants.ts';
import { useTradeBenefitsContact } from '../../useTradeBenefitsContact.ts';
import { trackTradeBenefitsEvent } from '../../tradeBenefitsTracking.ts';

import { type TradeBenefitsCurrentTier_viewer$key } from './__generated__/TradeBenefitsCurrentTier_viewer.graphql.ts';
import { type TradeBenefitsCurrentTier_tradeFirm$key } from './__generated__/TradeBenefitsCurrentTier_tradeFirm.graphql.ts';

import styles from './TradeBenefitsCurrentTier.scss';
import dibsCss from 'dibs-css';

type SectionKey = 'pricing' | 'purchaseProtection' | 'service' | 'publicity';

type AccordionItemData = {
    id: string;
    summary: ReactNode;
    content: (trigger: string) => ReactNode;
    openOnHashes?: readonly string[];
};

type SectionData = {
    key: SectionKey;
    title: ReactNode;
    items: AccordionItemData[];
};

const ItalicLargeTitle: FC<{ children: ReactNode }> = ({ children }) => (
    <span className={classnames(styles.italicLargeTitle, dibsCss.textHeaderXLargeItalic)}>
        {children}
    </span>
);

const ItalicSectionTitle: FC<{ children: ReactNode }> = ({ children }) => (
    <span className={classnames(styles.italicSectionTitle, dibsCss.sassyFontHeaderMediumItalic)}>
        {children}
    </span>
);

const AccordionContent: FC<{
    description: ReactNode;
    ctaLabel: ReactNode;
    ctaHref: string;
    trigger: string;
    ctaTrackingLabel: string;
}> = ({ description, ctaLabel, ctaHref, trigger, ctaTrackingLabel }) => (
    <>
        <p className={classnames(dibsCss.mt0, dibsCss.mbSmall)}>{description}</p>
        <Link
            href={ctaHref}
            target="_blank"
            onClick={() =>
                trackTradeBenefitsEvent({
                    stepInteractionName: 'drawer cta clicked',
                    trigger,
                    stepType: ctaTrackingLabel,
                })
            }
        >
            {ctaLabel}
        </Link>
    </>
);

const KNOWN_TIERS: ReadonlySet<TradeStatusCode> = new Set([
    TRADE_STATUS.BRONZE,
    TRADE_STATUS.SILVER,
    TRADE_STATUS.GOLD,
    TRADE_STATUS.PLATINUM,
]);

const MONEY_BACK_DAYS: Record<TradeStatusCode, number> = {
    [TRADE_STATUS.BRONZE]: 30,
    [TRADE_STATUS.SILVER]: 60,
    [TRADE_STATUS.GOLD]: 90,
    [TRADE_STATUS.PLATINUM]: 120,
};

const CASH_BACK_AMOUNT: Partial<Record<TradeStatusCode, number>> = {
    [TRADE_STATUS.GOLD]: 25,
    [TRADE_STATUS.PLATINUM]: 50,
};

const TIER_TITLES: Record<TradeStatusCode, ReactNode> = {
    [TRADE_STATUS.BRONZE]: (
        <FormattedMessage
            id="abt.TradeBenefitsCurrentTier.title.bronze"
            defaultMessage="Your Bronze Status <italic>Benefits</italic>"
            values={{ italic: content => <ItalicLargeTitle>{content}</ItalicLargeTitle> }}
        />
    ),
    [TRADE_STATUS.SILVER]: (
        <FormattedMessage
            id="abt.TradeBenefitsCurrentTier.title.silver"
            defaultMessage="Your Silver Status <italic>Benefits</italic>"
            values={{ italic: content => <ItalicLargeTitle>{content}</ItalicLargeTitle> }}
        />
    ),
    [TRADE_STATUS.GOLD]: (
        <FormattedMessage
            id="abt.TradeBenefitsCurrentTier.title.gold"
            defaultMessage="Your Gold Status <italic>Benefits</italic>"
            values={{ italic: content => <ItalicLargeTitle>{content}</ItalicLargeTitle> }}
        />
    ),
    [TRADE_STATUS.PLATINUM]: (
        <FormattedMessage
            id="abt.TradeBenefitsCurrentTier.title.platinum"
            defaultMessage="Your Platinum Status <italic>Benefits</italic>"
            values={{ italic: content => <ItalicLargeTitle>{content}</ItalicLargeTitle> }}
        />
    ),
};

const FIRMLESS_TITLE: ReactNode = (
    <FormattedMessage
        id="abt.TradeBenefitsCurrentTier.title.firmless"
        defaultMessage="Your <italic>Benefits</italic>"
        values={{ italic: content => <ItalicLargeTitle>{content}</ItalicLargeTitle> }}
    />
);

const SECTION_TITLES: Record<SectionKey, ReactNode> = {
    pricing: (
        <FormattedMessage
            id="abt.TradeBenefitsCurrentTier.pricing.title"
            defaultMessage="Exclusive <italic>Pricing & Tools</italic>"
            values={{ italic: content => <ItalicSectionTitle>{content}</ItalicSectionTitle> }}
        />
    ),
    purchaseProtection: (
        <FormattedMessage
            id="abt.TradeBenefitsCurrentTier.purchaseProtection.title"
            defaultMessage="Industry Leading <italic>Purchase Protection</italic>"
            values={{ italic: content => <ItalicSectionTitle>{content}</ItalicSectionTitle> }}
        />
    ),
    service: (
        <FormattedMessage
            id="abt.TradeBenefitsCurrentTier.service.title"
            defaultMessage="Exceptional <italic>Service</italic>"
            values={{ italic: content => <ItalicSectionTitle>{content}</ItalicSectionTitle> }}
        />
    ),
    publicity: (
        <FormattedMessage
            id="abt.TradeBenefitsCurrentTier.publicity.title"
            defaultMessage="Publicity <italic>Opportunities</italic>"
            values={{ italic: content => <ItalicSectionTitle>{content}</ItalicSectionTitle> }}
        />
    ),
};

const getPricingItems = (programCode: TradeStatusCode): AccordionItemData[] => {
    const items: AccordionItemData[] = [
        {
            id: 'net-price',
            summary: (
                <FormattedMessage
                    id="abt.TradeBenefitsCurrentTier.pricing.netPrice"
                    defaultMessage="Net Price on Furniture and Art"
                />
            ),
            content: trigger => (
                <AccordionContent
                    description={
                        <FormattedMessage
                            id="abt.TradeBenefitsCurrentTier.pricing.netPrice.content"
                            defaultMessage="Enjoy up to {percentage} in net price discounts on unique pieces from our exceptional supply of contemporary, vintage, and antique furniture and art."
                            values={{
                                percentage: (
                                    <FormattedNumber
                                        style="percent"
                                        value={NET_PRICE_PERCENTAGE / 100}
                                    />
                                ),
                            }}
                        />
                    }
                    ctaLabel={
                        <FormattedMessage
                            id="abt.TradeBenefitsCurrentTier.pricing.netPrice.cta"
                            defaultMessage="Shop with Net Price Discounts"
                        />
                    }
                    ctaHref="/search/?mv=true&netTradePriceDiscount=30"
                    trigger={trigger}
                    ctaTrackingLabel="shop-with-net-price-discounts"
                />
            ),
        },
        {
            id: 'tax-exempt',
            summary: (
                <FormattedMessage
                    id="abt.TradeBenefitsCurrentTier.pricing.taxExempt"
                    defaultMessage="Tax Exempt Purchasing"
                />
            ),
            content: trigger => (
                <AccordionContent
                    description={
                        <FormattedMessage
                            id="abt.TradeBenefitsCurrentTier.pricing.taxExempt.content"
                            defaultMessage="Enjoy tax free purchasing by uploading your verified tax resale certificates to your Trade 1st account. Once you upload your certificate, all account users will have access to tax free shopping."
                        />
                    }
                    ctaLabel={
                        <FormattedMessage
                            id="abt.TradeBenefitsCurrentTier.pricing.taxExempt.cta"
                            defaultMessage="Add a Certificate"
                        />
                    }
                    ctaHref="/my/account/taxExempt/"
                    trigger={trigger}
                    ctaTrackingLabel="add-a-certificate"
                />
            ),
        },
        {
            id: 'hold-requests',
            summary: (
                <FormattedMessage
                    id="abt.TradeBenefitsCurrentTier.pricing.holdRequests"
                    defaultMessage="Unlimited Hold Requests"
                />
            ),
            content: trigger => (
                <AccordionContent
                    description={
                        <FormattedMessage
                            id="abt.TradeBenefitsCurrentTier.pricing.holdRequests.content"
                            defaultMessage="Request unlimited holds on eligible pieces, giving your firm the flexibility to reserve items while final approvals are made with your team or client. Simply click on “Request a Hold” on the item page and complete a short message to the seller. You can view your current hold requests in the message center by filtering to “On Hold For You”."
                        />
                    }
                    ctaLabel={
                        <FormattedMessage
                            id="abt.TradeBenefitsCurrentTier.pricing.holdRequests.cta"
                            defaultMessage="Start Shopping Best Selling Designs"
                        />
                    }
                    ctaHref="/collections/best-sellers/?sort=newest"
                    trigger={trigger}
                    ctaTrackingLabel="start-shopping-best-selling-designs"
                />
            ),
        },
    ];

    const cashBack = CASH_BACK_AMOUNT[programCode];
    if (cashBack) {
        items.push({
            id: 'cash-back',
            summary: (
                <FormattedMessage
                    id="abt.TradeBenefitsCurrentTier.pricing.cashBack"
                    defaultMessage="Cash Back"
                />
            ),
            content: () => (
                <p className={classnames(dibsCss.mt0, dibsCss.mbXsmall)}>
                    <FormattedMessage
                        id="abt.TradeBenefitsCurrentTier.pricing.cashBack.content"
                        defaultMessage="Receive {amount} Cash Back for Every {points} Points Earned."
                        values={{
                            amount: (
                                <FormattedNumber
                                    style="currency"
                                    currency="USD"
                                    value={cashBack}
                                    maximumFractionDigits={0}
                                />
                            ),
                            points: <FormattedNumber value={5000} />,
                        }}
                    />
                </p>
            ),
        });
    }

    return items;
};

const getPurchaseProtectionItems = (programCode: TradeStatusCode): AccordionItemData[] => [
    {
        id: 'authenticity-guarantee',
        summary: (
            <FormattedMessage
                id="abt.TradeBenefitsCurrentTier.purchaseProtection.authenticity"
                defaultMessage="Authenticity Guarantee"
            />
        ),
        content: trigger => (
            <AccordionContent
                description={
                    <FormattedMessage
                        id="abt.TradeBenefitsCurrentTier.purchaseProtection.authenticity.content"
                        defaultMessage="We understand the importance of quality, so if any item is found to be non-authentic within one year, we will provide a full refund."
                    />
                }
                ctaLabel={
                    <FormattedMessage
                        id="abt.TradeBenefitsCurrentTier.purchaseProtection.authenticity.cta"
                        defaultMessage="Contact Support"
                    />
                }
                ctaHref={CUSTOMER_SUPPORT_URL}
                trigger={trigger}
                ctaTrackingLabel="contact-support"
            />
        ),
    },
    {
        id: 'price-match',
        summary: (
            <FormattedMessage
                id="abt.TradeBenefitsCurrentTier.purchaseProtection.priceMatch"
                defaultMessage="Price Match Guarantee"
            />
        ),
        content: trigger => (
            <AccordionContent
                description={
                    <FormattedMessage
                        id="abt.TradeBenefitsCurrentTier.purchaseProtection.priceMatch.content"
                        defaultMessage="We want to ensure you always get the best value for your client. If the same seller has the item listed lower elsewhere, we’ll match it."
                    />
                }
                ctaLabel={
                    <FormattedMessage
                        id="abt.TradeBenefitsCurrentTier.purchaseProtection.priceMatch.cta"
                        defaultMessage="Learn More About Price Match Guarantee"
                    />
                }
                ctaHref={getBuyerSupportLink(
                    '/en-us/articles/15203720988699-Price-Match-Guarantee'
                )}
                trigger={trigger}
                ctaTrackingLabel="learn-more-about-price-match-guarantee"
            />
        ),
    },
    {
        id: 'return-policy',
        summary: (
            <FormattedMessage
                id="abt.TradeBenefitsCurrentTier.purchaseProtection.returnPolicy"
                defaultMessage="Expanded Return Policy"
            />
        ),
        content: trigger => (
            <AccordionContent
                description={
                    <FormattedMessage
                        id="abt.TradeBenefitsCurrentTier.purchaseProtection.returnPolicy.content"
                        defaultMessage="Enjoy a minimum of 14 days to return an item plus waived restocking fees — even if the seller’s standard policy doesn’t allow returns."
                    />
                }
                ctaLabel={
                    <FormattedMessage
                        id="abt.TradeBenefitsCurrentTier.purchaseProtection.returnPolicy.cta"
                        defaultMessage="Learn More About Returns"
                    />
                }
                ctaHref={getBuyerSupportLink('/en-us/articles/33803789395867-Trade-Return-Policy')}
                trigger={trigger}
                ctaTrackingLabel="learn-more-about-returns"
            />
        ),
    },
    {
        id: 'money-back-guarantee',
        openOnHashes: [PURCHASE_PROTECTION_SECTION_ID],
        summary: (
            <FormattedMessage
                id="abt.TradeBenefitsCurrentTier.purchaseProtection.moneyBack"
                defaultMessage="{days} Day Money-Back Guarantee"
                values={{ days: MONEY_BACK_DAYS[programCode] }}
            />
        ),
        content: trigger => (
            <AccordionContent
                description={
                    <FormattedMessage
                        id="abt.TradeBenefitsCurrentTier.purchaseProtection.moneyBack.content"
                        defaultMessage="If your item is not as described, is damaged in transit, or does not arrive, contact us within {days} days and you may be eligible for a refund. We’ll work with you to make it right to keep your project on track."
                        values={{ days: MONEY_BACK_DAYS[programCode] }}
                    />
                }
                ctaLabel={
                    <FormattedMessage
                        id="abt.TradeBenefitsCurrentTier.purchaseProtection.moneyBack.cta"
                        defaultMessage="Learn More About Money-Back Guarantee"
                    />
                }
                ctaHref={getBuyerSupportLink(
                    '/en-us/articles/33804106728731-Trade-Money-Back-Guarantee'
                )}
                trigger={trigger}
                ctaTrackingLabel="learn-more-about-money-back-guarantee"
            />
        ),
    },
];

const getServiceItems = ({
    programCode,
    isAssigned,
    repEmail,
}: {
    programCode: TradeStatusCode;
    isAssigned: boolean;
    repEmail: string | null | undefined;
}): AccordionItemData[] => {
    const isSilverPlus = SILVER_PLUS_CODES.has(programCode);
    const isGoldPlus = programCode === TRADE_STATUS.GOLD || programCode === TRADE_STATUS.PLATINUM;

    const items: AccordionItemData[] = [];

    if (isSilverPlus) {
        const hasRep = isAssigned && !!repEmail;
        items.push(
            {
                id: 'account-rep',
                summary: (
                    <FormattedMessage
                        id="abt.TradeBenefitsCurrentTier.service.accountRep"
                        defaultMessage="Your Dedicated Account Representative"
                    />
                ),
                content: trigger => (
                    <AccordionContent
                        description={
                            <FormattedMessage
                                id="abt.TradeBenefitsCurrentTier.service.accountRep.content"
                                defaultMessage="Lean on your dedicated account representative as your primary contact for discussing project needs, planning ahead, and maximizing the benefits of your Trade 1st status."
                            />
                        }
                        ctaLabel={
                            hasRep ? (
                                <FormattedMessage
                                    id="abt.TradeBenefitsCurrentTier.service.accountRep.cta.assigned"
                                    defaultMessage="Contact Your Representative"
                                />
                            ) : (
                                <FormattedMessage
                                    id="abt.TradeBenefitsCurrentTier.service.accountRep.cta.unassigned"
                                    defaultMessage="Contact Trade Sales"
                                />
                            )
                        }
                        ctaHref={`mailto:${hasRep ? repEmail : TRADE_SALES_EMAIL}`}
                        trigger={trigger}
                        ctaTrackingLabel={
                            hasRep ? 'contact-your-representative' : 'contact-trade-sales'
                        }
                    />
                ),
            },
            {
                id: 'sourcing-specialists',
                summary: (
                    <FormattedMessage
                        id="abt.TradeBenefitsCurrentTier.service.sourcing"
                        defaultMessage="Sourcing Specialists"
                    />
                ),
                content: trigger => (
                    <AccordionContent
                        description={
                            <FormattedMessage
                                id="abt.TradeBenefitsCurrentTier.service.sourcing.content"
                                defaultMessage="Save time by leveraging our team as an extension of yours to source pieces for a full project or track down a single elusive item. Share details on the item(s) you are searching for, and our experts will reach out to help you find the perfect pieces."
                            />
                        }
                        ctaLabel={
                            <FormattedMessage
                                id="abt.TradeBenefitsCurrentTier.service.sourcing.cta"
                                defaultMessage="Submit a Sourcing Request Form"
                            />
                        }
                        ctaHref="/trade/sourcing/"
                        trigger={trigger}
                        ctaTrackingLabel="submit-a-sourcing-request-form"
                    />
                ),
            }
        );
    }

    if (isGoldPlus) {
        items.push({
            id: 'trade-support-specialist',
            summary: (
                <FormattedMessage
                    id="abt.TradeBenefitsCurrentTier.service.tradeSupport"
                    defaultMessage="Trade Support Specialist"
                />
            ),
            content: trigger => (
                <AccordionContent
                    description={
                        <FormattedMessage
                            id="abt.TradeBenefitsCurrentTier.service.tradeSupport.content"
                            defaultMessage="If you have questions or run into an issue with an order, your dedicated Trade Support Specialist understands the unique complexities of design projects and installs, and is able to provide expert assistance so you can keep your shopping experience running smoothly."
                        />
                    }
                    ctaLabel={
                        <FormattedMessage
                            id="abt.TradeBenefitsCurrentTier.service.tradeSupport.cta"
                            defaultMessage="Contact Support"
                        />
                    }
                    ctaHref={CUSTOMER_SUPPORT_URL}
                    trigger={trigger}
                    ctaTrackingLabel="contact-support"
                />
            ),
        });
    } else {
        items.push({
            id: 'customer-support',
            summary: (
                <FormattedMessage
                    id="abt.TradeBenefitsCurrentTier.service.customerSupport"
                    defaultMessage="Customer Support"
                />
            ),
            content: trigger => (
                <AccordionContent
                    description={
                        <FormattedMessage
                            id="abt.TradeBenefitsCurrentTier.service.customerSupport.content"
                            defaultMessage="If you have questions or run into an issue with an order, our Customer Support team offers responsive and reliable assistance so you can quickly get the help you need and keep your shopping experience running smoothly."
                        />
                    }
                    ctaLabel={
                        <FormattedMessage
                            id="abt.TradeBenefitsCurrentTier.service.customerSupport.cta"
                            defaultMessage="Contact Support"
                        />
                    }
                    ctaHref={CUSTOMER_SUPPORT_URL}
                    trigger={trigger}
                    ctaTrackingLabel="contact-support"
                />
            ),
        });
    }

    return items;
};

const getPublicityItems = (programCode: TradeStatusCode): AccordionItemData[] => {
    const isSilverPlus = SILVER_PLUS_CODES.has(programCode);
    const isPlatinum = programCode === TRADE_STATUS.PLATINUM;

    const items: AccordionItemData[] = [
        {
            id: 'design-directory',
            summary: (
                <FormattedMessage
                    id="abt.TradeBenefitsCurrentTier.publicity.designDirectory"
                    defaultMessage="Firm Portfolio in 1stDibs’ Design Directory"
                />
            ),
            content: trigger => (
                <AccordionContent
                    description={
                        <FormattedMessage
                            id="abt.TradeBenefitsCurrentTier.publicity.designDirectory.content"
                            defaultMessage="Create a Firm Profile and share your portfolio of projects in the 1stDibs Design Directory. Having a 1stDibs portfolio is a great way to increase your exposure among our community of over 4.3M registered users. Plus, it gets your work in front of our internal editorial, social and marketing teams."
                        />
                    }
                    ctaLabel={
                        <FormattedMessage
                            id="abt.TradeBenefitsCurrentTier.publicity.designDirectory.cta"
                            defaultMessage="Update Your Firm Portfolio"
                        />
                    }
                    ctaHref="/my/firm-portfolio/"
                    trigger={trigger}
                    ctaTrackingLabel="update-your-firm-portfolio"
                />
            ),
        },
    ];

    if (isSilverPlus) {
        items.push({
            id: 'publicity-opportunities',
            summary: (
                <FormattedMessage
                    id="abt.TradeBenefitsCurrentTier.publicity.opportunities"
                    defaultMessage="Access to 1stDibs Publicity Opportunities"
                />
            ),
            content: trigger => (
                <AccordionContent
                    description={
                        <FormattedMessage
                            id="abt.TradeBenefitsCurrentTier.publicity.opportunities.content"
                            defaultMessage="Get considered for placement on our email, onsite, social media, or Editorial channels, and increase your exposure to our global network of customers."
                        />
                    }
                    ctaLabel={
                        <FormattedMessage
                            id="abt.TradeBenefitsCurrentTier.publicity.opportunities.cta"
                            defaultMessage="Upload Projects for Consideration"
                        />
                    }
                    ctaHref="/my/firm-portfolio/"
                    trigger={trigger}
                    ctaTrackingLabel="upload-projects-for-consideration"
                />
            ),
        });
    }

    if (isPlatinum) {
        items.push({
            id: 'consideration-1d50',
            summary: (
                <FormattedMessage
                    id="abt.TradeBenefitsCurrentTier.publicity.oneD50"
                    defaultMessage="Consideration for 1D50"
                />
            ),
            content: trigger => (
                <AccordionContent
                    description={
                        <FormattedMessage
                            id="abt.TradeBenefitsCurrentTier.publicity.oneD50.content"
                            defaultMessage="Enjoy preferential consideration for our esteemed “best of” list, the 1stDibs 50, which honors 50 of today’s most visionary designers."
                        />
                    }
                    ctaLabel={
                        <FormattedMessage
                            id="abt.TradeBenefitsCurrentTier.publicity.oneD50.cta"
                            defaultMessage="Check Out the 1stDibs 50"
                        />
                    }
                    ctaHref="/1stdibs-50/"
                    trigger={trigger}
                    ctaTrackingLabel="check-out-the-1stdibs-50"
                />
            ),
        });
    }

    return items;
};

const getSections = ({
    programCode,
    isAssigned,
    repEmail,
}: {
    programCode: TradeStatusCode;
    isAssigned: boolean;
    repEmail: string | null | undefined;
}): SectionData[] => [
    {
        key: 'pricing',
        title: SECTION_TITLES.pricing,
        items: getPricingItems(programCode),
    },
    {
        key: 'purchaseProtection',
        title: SECTION_TITLES.purchaseProtection,
        items: getPurchaseProtectionItems(programCode),
    },
    {
        key: 'service',
        title: SECTION_TITLES.service,
        items: getServiceItems({ programCode, isAssigned, repEmail }),
    },
    {
        key: 'publicity',
        title: SECTION_TITLES.publicity,
        items: getPublicityItems(programCode),
    },
];

const SECTION_ANCHOR_IDS: Partial<Record<SectionKey, string>> = {
    purchaseProtection: PURCHASE_PROTECTION_SECTION_ID,
};

const Section: FC<{ section: SectionData }> = ({ section: { key, title, items } }) => (
    <div id={SECTION_ANCHOR_IDS[key]} className={SECTION_ANCHOR_IDS[key] && styles.deepLinkAnchor}>
        <HeadingLevel>
            {Heading => (
                <Heading
                    className={classnames(
                        dibsCss.sassyFontHeaderMedium,
                        dibsCss.mt0,
                        dibsCss.mbSmall
                    )}
                >
                    {title}
                </Heading>
            )}
        </HeadingLevel>
        <ul className={classnames(styles.list, dibsCss.p0, dibsCss.m0)}>
            {items.map(({ id, summary, content, openOnHashes }) => (
                <TradeBenefitsAccordionItem
                    key={id}
                    summary={summary}
                    contentId={id}
                    openOnHashes={openOnHashes}
                    onOpen={() =>
                        trackTradeBenefitsEvent({
                            stepInteractionName: 'benefit drawer opened',
                            trigger: id,
                        })
                    }
                >
                    {content(id)}
                </TradeBenefitsAccordionItem>
            ))}
        </ul>
    </div>
);

const viewerFragment = graphql`
    fragment TradeBenefitsCurrentTier_viewer on Viewer {
        ...useTradeBenefitsContact_viewer
    }
`;

const tradeFirmFragment = graphql`
    fragment TradeBenefitsCurrentTier_tradeFirm on TradeFirmType {
        ...useTradeBenefitsContact_tradeFirm
        ...TradeBenefitsNextStatus_tradeFirm
        rewards {
            currentProgram {
                programInfo {
                    code
                }
            }
        }
    }
`;

const isKnownTier = (code: string | null | undefined): code is TradeStatusCode =>
    !!code && KNOWN_TIERS.has(code as TradeStatusCode);

const TradeBenefitsCurrentTier: FC<{
    viewer: TradeBenefitsCurrentTier_viewer$key;
    tradeFirm: TradeBenefitsCurrentTier_tradeFirm$key | null | undefined;
    isFirmless?: boolean;
}> = ({ viewer: viewerRef, tradeFirm: tradeFirmRef, isFirmless = false }) => {
    const viewer = useFragment(viewerFragment, viewerRef);
    const tradeFirm = useFragment(tradeFirmFragment, tradeFirmRef);
    const { isAssigned, repEmail } = useTradeBenefitsContact({ viewer, tradeFirm });
    const programCode = tradeFirm?.rewards?.currentProgram?.programInfo?.code;

    // Firmless members have no tier; show the base (Bronze) benefit set in the
    // tile-less Platinum layout under a generic heading.
    const tierCode = isFirmless ? TRADE_STATUS.BRONZE : programCode;
    if (!isKnownTier(tierCode)) {
        return null;
    }

    const sections = getSections({ programCode: tierCode, isAssigned, repEmail });
    const isFullWidth = isFirmless || tierCode === TRADE_STATUS.PLATINUM;
    const title = isFirmless ? FIRMLESS_TITLE : TIER_TITLES[tierCode];

    return (
        <div className={classnames(styles.wrapper, dibsCss.mbXlarge)}>
            <HeadingLevel>
                {Heading => {
                    const headingAndSections = (
                        <div id={CURRENT_TIER_SECTION_ID}>
                            <Heading
                                className={classnames(
                                    styles.title,
                                    dibsCss.textHeaderXLargeNormal,
                                    dibsCss.mt0,
                                    dibsCss.mbLarger
                                )}
                            >
                                {title}
                            </Heading>
                            <div
                                className={classnames(
                                    isFullWidth ? styles.sectionsGridPlatinum : styles.sectionsGrid,
                                    dibsCss.grid,
                                    dibsCss.gapLarge
                                )}
                            >
                                {sections.map(section => (
                                    <Section key={section.key} section={section} />
                                ))}
                            </div>
                        </div>
                    );

                    if (isFullWidth) {
                        return headingAndSections;
                    }

                    return (
                        <div
                            className={classnames(
                                styles.layout,
                                dibsCss.grid,
                                dibsCss.gapLarge,
                                dibsCss.mbXlarge
                            )}
                        >
                            {headingAndSections}
                            <div className={styles.nextStatus}>
                                <TradeBenefitsNextStatus tradeFirm={tradeFirm} />
                            </div>
                        </div>
                    );
                }}
            </HeadingLevel>
        </div>
    );
};

export { TradeBenefitsCurrentTier };
